import {
    Row,
    Col,
    Card,
    Statistic,
    Typography,
    Space,
    Select,
    DatePicker,
    Spin
} from "antd";
import {
    UserOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    MinusCircleOutlined,
    TeamOutlined,
    CalendarOutlined,
    BarChartOutlined,
    PieChartOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { loadUsers } from "../store/app/app.slice";
import moment from "moment";
import { UserDataDto } from "acc-rest-client";
import ReactApexChart from "react-apexcharts";

const { Title } = Typography;
const { RangePicker } = DatePicker;

function Statistics() {
    const dispatch = useDispatch();
    const { users, isUserLoading } = useSelector((state) => state.app);
    const [dateRange, setDateRange] = useState([moment('08.05.25', 'DD.MM.YY'), moment()]);
    const [selectedCountry, setSelectedCountry] = useState('all');

    useEffect(() => {
        // Only fetch data if not already loaded
        if (!users || users.length === 0) {
            dispatch(loadUsers({}));
        }
    }, [dispatch, users]);


    // Calculate statistics using the same logic as Members.jsx
    const calculateStats = () => {
        if (!users) {
            return {};
        }

        const filteredUsers = users.filter(user => {
            const userDate = moment(user.createdTimestamp);
            const isInDateRange = userDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
            const isInCountry = selectedCountry === 'all' || user.jamaat === selectedCountry;
            return isInDateRange && isInCountry;
        });

        const stats = {
            total: filteredUsers.length,
            // Using exact same status logic as Members.jsx with string values
            active: filteredUsers.filter(u => u.approvalStatus === "APPROVED").length,
            pending: filteredUsers.filter(u => u.approvalStatus === "APPROVAL_RQUESTED").length,
            unverified: filteredUsers.filter(u => u.approvalStatus === "NEW_REGISTERED_ACCOUNT" && !u.emailVerified).length,
            tajneedMissing: filteredUsers.filter(u => u.approvalStatus === "NEW_REGISTERED_ACCOUNT" && u.emailVerified).length,
            rejected: filteredUsers.filter(u => u.approvalStatus === "REJECTED").length,
            deactivated: filteredUsers.filter(u => u.approvalStatus === "DEACTIVATED").length,
            // Using exact same gender logic as Members.jsx with string values
            male: filteredUsers.filter(u => u.gender === "MALE").length,
            female: filteredUsers.filter(u => u.gender === "FEMALE").length,
            unknown: filteredUsers.filter(u => !u.gender || (u.gender !== "MALE" && u.gender !== "FEMALE")).length,
        };

        // Calculate average age using birthdate field
        const usersWithAge = filteredUsers.filter(u => u.birthdate);
        const totalAge = usersWithAge.reduce((sum, user) => {
            const age = moment().diff(moment(user.birthdate), 'years');
            return sum + age;
        }, 0);
        stats.averageAge = usersWithAge.length > 0 ? Math.round(totalAge / usersWithAge.length) : 0;

        return stats;
    };

    // Get country distribution using jamaat field (same as Members.jsx)
    const getCountryDistribution = () => {
        if (!users) return [];
        
        const countryMap = {};
        users.forEach(user => {
            const country = user.jamaat || 'Unknown';
            countryMap[country] = (countryMap[country] || 0) + 1;
        });

        return Object.entries(countryMap)
            .map(([country, count]) => ({ name: country, value: count }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    };

    // Get countries distribution using country i18n field
    const getCountriesDistribution = () => {
        if (!users) return [];
        
        const countriesMap = {};
        users.forEach(user => {
            // Extract country from i18n object - use defaultLabel or key
            let country = 'Unknown';
            if (user.country) {
                if (typeof user.country === 'object') {
                    country = user.country.defaultLabel || user.country.key || 'Unknown';
                } else {
                    country = user.country;
                }
            }
            countriesMap[country] = (countriesMap[country] || 0) + 1;
        });

        return Object.entries(countriesMap)
            .map(([country, count]) => ({ name: country, value: count }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    };

    // Get gender distribution using exact same logic as Members.jsx
    const getGenderDistribution = () => {
        if (!users) return [];
        
        const stats = calculateStats();
        return [
            { name: 'Male', value: stats.male },
            { name: 'Female', value: stats.female },
            { name: 'Unknown', value: stats.unknown }
        ].filter(item => item.value > 0);
    };

    // Get registration trends using createdTimestamp field
    const getRegistrationTrends = () => {
        if (!users) return { daily: [], monthly: [] };

        const filteredUsers = users.filter(user => {
            const userDate = moment(user.createdTimestamp);
            return userDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
        });

        // Daily registrations
        const dailyMap = {};
        filteredUsers.forEach(user => {
            const date = moment(user.createdTimestamp).format('YYYY-MM-DD');
            dailyMap[date] = (dailyMap[date] || 0) + 1;
        });

        const dailyData = Object.entries(dailyMap)
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => a.date.localeCompare(b.date));

        // Monthly registrations
        const monthlyMap = {};
        filteredUsers.forEach(user => {
            const month = moment(user.createdTimestamp).format('YYYY-MM');
            monthlyMap[month] = (monthlyMap[month] || 0) + 1;
        });

        const monthlyData = Object.entries(monthlyMap)
            .map(([month, count]) => ({ month, count }))
            .sort((a, b) => a.month.localeCompare(b.month));

        return { daily: dailyData, monthly: monthlyData };
    };

    // Get status distribution using exact same logic as Members.jsx
    const getStatusDistribution = () => {
        if (!users) return [];
        
        const stats = calculateStats();
        return [
            { name: 'Active', value: stats.active },
            { name: 'Pending', value: stats.pending },
            { name: 'Unverified', value: stats.unverified },
            { name: 'Tajneed Missing', value: stats.tajneedMissing },
            { name: 'Rejected', value: stats.rejected },
            { name: 'Deactivated', value: stats.deactivated }
        ].filter(item => item.value > 0);
    };

    // Get age distribution using birthdate field
    const getAgeDistribution = () => {
        if (!users) return [];
        
        const ageGroups = {
            '0-18': 0,
            '19-30': 0,
            '31-50': 0,
            '51-65': 0,
            '65+': 0
        };

        users.forEach(user => {
            if (user.birthdate) {
                const age = moment().diff(moment(user.birthdate), 'years');
                if (age <= 18) ageGroups['0-18']++;
                else if (age <= 30) ageGroups['19-30']++;
                else if (age <= 50) ageGroups['31-50']++;
                else if (age <= 65) ageGroups['51-65']++;
                else ageGroups['65+']++;
            }
        });

        return Object.entries(ageGroups)
            .map(([range, count]) => ({ name: range, value: count }))
            .filter(item => item.value > 0);
    };

    // Get city distribution using city field
    const getCityDistribution = () => {
        if (!users) return [];
        
        const cityMap = {};
        users.forEach(user => {
            const city = user.city || 'Unknown';
            cityMap[city] = (cityMap[city] || 0) + 1;
        });

        return Object.entries(cityMap)
            .map(([city, count]) => ({ name: city, value: count }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    };

    // Get state distribution using state field
    const getStateDistribution = () => {
        if (!users) return [];
        
        const stateMap = {};
        users.forEach(user => {
            const state = user.state || 'Unknown';
            stateMap[state] = (stateMap[state] || 0) + 1;
        });

        return Object.entries(stateMap)
            .map(([state, count]) => ({ name: state, value: count }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    };

    const stats = calculateStats();
    const countryDistribution = getCountryDistribution();
    const countriesDistribution = getCountriesDistribution();
    const genderDistribution = getGenderDistribution();
    const registrationTrends = getRegistrationTrends();
    const statusDistribution = getStatusDistribution();
    const ageDistribution = getAgeDistribution();
    const cityDistribution = getCityDistribution();
    const stateDistribution = getStateDistribution();

    // Get unique countries for filter using jamaat field
    const countries = [...new Set(users?.map(u => u.jamaat).filter(Boolean))].sort();

    // Chart configurations for ApexCharts
    const countryChartOptions = {
        chart: {
            type: 'bar',
            height: 350
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: countryDistribution.map(item => item.name),
            labels: {
                rotate: -45
            }
        },
        yaxis: {
            title: {
                text: 'Number of Members'
            }
        },
        title: {
            text: 'Top Jamaats by Member Count',
            align: 'center'
        },
        colors: ['#755B48']
    };

    const countryChartSeries = [{
        name: 'Members',
        data: countryDistribution.map(item => item.value)
    }];

    const countriesChartOptions = {
        chart: {
            type: 'bar',
            height: 350
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: countriesDistribution.map(item => item.name),
            labels: {
                rotate: -45
            }
        },
        yaxis: {
            title: {
                text: 'Number of Members'
            }
        },
        title: {
            text: 'Top Countries by Member Count',
            align: 'center'
        },
        colors: ['#13c2c2']
    };

    const countriesChartSeries = [{
        name: 'Members',
        data: countriesDistribution.map(item => item.value)
    }];

    const ageChartOptions = {
        chart: {
            type: 'bar',
            height: 350
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: ageDistribution.map(item => item.name)
        },
        yaxis: {
            title: {
                text: 'Number of Members'
            }
        },
        title: {
            text: 'Age Distribution',
            align: 'center'
        },
        colors: ['#52c41a']
    };

    const ageChartSeries = [{
        name: 'Members',
        data: ageDistribution.map(item => item.value)
    }];

    const cityChartOptions = {
        chart: {
            type: 'bar',
            height: 350
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: cityDistribution.map(item => item.name),
            labels: {
                rotate: -45
            }
        },
        yaxis: {
            title: {
                text: 'Number of Members'
            }
        },
        title: {
            text: 'Top Cities by Member Count',
            align: 'center'
        },
        colors: ['#1890ff']
    };

    const cityChartSeries = [{
        name: 'Members',
        data: cityDistribution.map(item => item.value)
    }];

    const dailyTrendOptions = {
        chart: {
            type: 'line',
            height: 350
        },
        stroke: {
            curve: 'smooth'
        },
        xaxis: {
            categories: registrationTrends.daily.map(item => item.date)
        },
        yaxis: {
            title: {
                text: 'Number of Registrations'
            }
        },
        title: {
            text: 'Daily Registration Trend',
            align: 'center'
        },
        colors: ['#1890ff']
    };

    const dailyTrendSeries = [{
        name: 'Registrations',
        data: registrationTrends.daily.map(item => item.count)
    }];

    const monthlyTrendOptions = {
        chart: {
            type: 'line',
            height: 350
        },
        stroke: {
            curve: 'smooth'
        },
        xaxis: {
            categories: registrationTrends.monthly.map(item => item.month)
        },
        yaxis: {
            title: {
                text: 'Number of Registrations'
            }
        },
        title: {
            text: 'Monthly Registration Trend',
            align: 'center'
        },
        colors: ['#722ed1']
    };

    const monthlyTrendSeries = [{
        name: 'Registrations',
        data: registrationTrends.monthly.map(item => item.count)
    }];

    if (isUserLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!users || users.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Title level={3}>No data available</Title>
                <p>No users found to display statistics.</p>
            </div>
        );
    }

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title="PSO App Users Statistics Dashboard"
                            extra={
                                <Space>
                                    <RangePicker
                                        value={dateRange}
                                        onChange={setDateRange}
                                        format="YYYY-MM-DD"
                                    />
                                    <Select
                                        value={selectedCountry}
                                        onChange={setSelectedCountry}
                                        style={{ width: 200 }}
                                        placeholder="Select Jamaat"
                                    >
                                        <Select.Option value="all">All Jamaats</Select.Option>
                                        {countries.map(country => (
                                            <Select.Option key={country} value={country}>
                                                {country}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Space>
                            }
                        >
                            {/* Overview Statistics */}
                            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Total Members"
                                            value={stats.total}
                                            prefix={<UserOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Active Members"
                                            value={stats.active}
                                            prefix={<CheckCircleOutlined />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Pending Approval"
                                            value={stats.pending}
                                            prefix={<ClockCircleOutlined />}
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Average Age"
                                            value={stats.averageAge}
                                            suffix="years"
                                            prefix={<CalendarOutlined />}
                                            valueStyle={{ color: '#722ed1' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Charts Row 1 - Status and Gender Summary */}
                            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                <Col xs={24} lg={12}>
                                    <Card title="Status Summary" extra={<PieChartOutlined />} style={{ padding: '16px' }}>
                                        <Row gutter={[16, 16]} style={{ padding: '8px 0' }}>
                                            <Col span={12}>
                                                <Statistic title="Active" value={stats.active} valueStyle={{ color: '#52c41a' }} />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic title="Pending" value={stats.pending} valueStyle={{ color: '#faad14' }} />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic title="Unverified" value={stats.unverified} valueStyle={{ color: '#faad14' }} />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic title="Tajneed Missing" value={stats.tajneedMissing} valueStyle={{ color: '#fa8c16' }} />
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card title="Gender Summary" extra={<PieChartOutlined />} style={{ padding: '16px' }}>
                                        <Row gutter={[16, 16]} style={{ padding: '8px 0' }}>
                                            <Col span={12}>
                                                <Statistic title="Male" value={stats.male} valueStyle={{ color: '#1890ff' }} />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic title="Female" value={stats.female} valueStyle={{ color: '#eb2f96' }} />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic title="Unknown" value={stats.unknown} valueStyle={{ color: '#8c8c8c' }} />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic title="Total" value={stats.total} valueStyle={{ color: '#722ed1' }} />
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Charts Row 2 */}
                            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                <Col xs={24} lg={12}>
                                    <Card title="Top Jamaats" extra={<BarChartOutlined />} style={{ padding: '16px' }}>
                                        <ReactApexChart 
                                            options={countryChartOptions} 
                                            series={countryChartSeries} 
                                            type="bar" 
                                            height={300} 
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card title="Top Countries" extra={<BarChartOutlined />} style={{ padding: '16px' }}>
                                        <ReactApexChart 
                                            options={countriesChartOptions} 
                                            series={countriesChartSeries} 
                                            type="bar" 
                                            height={300} 
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Charts Row 3 */}
                            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                <Col xs={24} lg={12}>
                                    <Card title="Age Groups" extra={<BarChartOutlined />} style={{ padding: '16px' }}>
                                        <ReactApexChart 
                                            options={ageChartOptions} 
                                            series={ageChartSeries} 
                                            type="bar" 
                                            height={300} 
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card title="Top Cities" extra={<BarChartOutlined />} style={{ padding: '16px' }}>
                                        <ReactApexChart 
                                            options={cityChartOptions} 
                                            series={cityChartSeries} 
                                            type="bar" 
                                            height={300} 
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Charts Row 4 */}
                            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                <Col xs={24} lg={12}>
                                    <Card title="Daily Registration Trend" extra={<BarChartOutlined />} style={{ padding: '16px' }}>
                                        <ReactApexChart 
                                            options={dailyTrendOptions} 
                                            series={dailyTrendSeries} 
                                            type="line" 
                                            height={300} 
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card title="Monthly Registration Trend" extra={<BarChartOutlined />} style={{ padding: '16px' }}>
                                        <ReactApexChart 
                                            options={monthlyTrendOptions} 
                                            series={monthlyTrendSeries} 
                                            type="line" 
                                            height={300} 
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Additional Statistics */}
                            <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
                                <Col xs={24} sm={8}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Unverified Accounts"
                                            value={stats.unverified}
                                            prefix={<ExclamationCircleOutlined />}
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Tajneed Missing"
                                            value={stats.tajneedMissing}
                                            prefix={<ExclamationCircleOutlined />}
                                            valueStyle={{ color: '#fa8c16' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Rejected Accounts"
                                            value={stats.rejected}
                                            prefix={<MinusCircleOutlined />}
                                            valueStyle={{ color: '#f5222d' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* More Statistics */}
                            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                                <Col xs={24} sm={8}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Deactivated Accounts"
                                            value={stats.deactivated}
                                            prefix={<MinusCircleOutlined />}
                                            valueStyle={{ color: '#8c8c8c' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Male Members"
                                            value={stats.male}
                                            prefix={<TeamOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card style={{ padding: '16px', textAlign: 'center' }}>
                                        <Statistic
                                            title="Female Members"
                                            value={stats.female}
                                            prefix={<TeamOutlined />}
                                            valueStyle={{ color: '#eb2f96' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}

export default Statistics;
